import { conns } from '@cfg/axiosConfig.tsx';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';

export type CashBoxType = 'CASH' | 'DIGITAL_IMMEDIATE' | 'DIGITAL_CURRENT' | 'DIGITAL_NON_CURRENT' | 'INTERNAL';

export interface CashboxPaymentMethodRef {
  id: number;
  name: string;
}

export interface Cashbox {
  id: number;
  name: string;
  box_type: CashBoxType;
  payment_enabled: boolean;
  collection_enabled: boolean;
  requires_closure: boolean;
  created_at: string | null;
  payment_methods: CashboxPaymentMethodRef[];
}

export interface CashboxCreatePayload {
  name: string;
  box_type?: CashBoxType;
  payment_enabled?: boolean;
  collection_enabled?: boolean;
  requires_closure?: boolean;
}

export interface CashboxUpdatePayload extends Partial<CashboxCreatePayload> {}

const CASHBOXES_URL_KEY = 'erp:payment:cashboxes';
export const CASHBOXES_QUERY_KEY = ['erp', 'payments', 'cashboxes'] as const;

type CashboxesQueryResult<TData = Cashbox[]> = UseQueryResult<TData, Error>;

type CashboxesQueryOptions<TData> = Omit<
  UseQueryOptions<Cashbox[], Error, TData>,
  'queryKey' | 'queryFn'
>;

export const fetchCashboxes = async (): Promise<Cashbox[]> => {
  const { data } = await conns<Cashbox[]>(CASHBOXES_URL_KEY).get();
  return data ?? [];
};

export const useCashboxesQuery = <TData = Cashbox[]>(
  options?: CashboxesQueryOptions<TData>,
): CashboxesQueryResult<TData> => {
  return useQuery<Cashbox[], Error, TData>({
    queryKey: CASHBOXES_QUERY_KEY,
    queryFn: fetchCashboxes,
    staleTime: 30_000,
    ...options,
  });
};

export const createCashbox = async (payload: CashboxCreatePayload): Promise<Cashbox> => {
  const { data } = await conns<Cashbox>(CASHBOXES_URL_KEY).post(payload);
  return data;
};

export const updateCashbox = async (
  id: number,
  payload: CashboxUpdatePayload,
): Promise<Cashbox> => {
  const { data } = await conns<Cashbox>(CASHBOXES_URL_KEY, [id]).put(payload);
  return data;
};

interface MutationOptions<TResult> {
  onSuccess?: (data: TResult) => void;
  onError?: (error: Error) => void;
  invalidate?: boolean;
}

type CreateCashboxMutation = UseMutationResult<Cashbox, Error, CashboxCreatePayload>;
type UpdateCashboxMutation = UseMutationResult<Cashbox, Error, { id: number; payload: CashboxUpdatePayload }>;

export const useCreateCashboxMutation = (
  options?: MutationOptions<Cashbox>,
): CreateCashboxMutation => {
  const queryClient = useQueryClient();
  return useMutation<Cashbox, Error, CashboxCreatePayload>({
    mutationFn: createCashbox,
    onSuccess: (data) => {
      if (options?.invalidate !== false) {
        queryClient.invalidateQueries({ queryKey: CASHBOXES_QUERY_KEY });
      }
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
};

export const useUpdateCashboxMutation = (
  options?: MutationOptions<Cashbox>,
): UpdateCashboxMutation => {
  const queryClient = useQueryClient();
  return useMutation<Cashbox, Error, { id: number; payload: CashboxUpdatePayload }>({
    mutationFn: ({ id, payload }) => updateCashbox(id, payload),
    onSuccess: (data) => {
      if (options?.invalidate !== false) {
        queryClient.invalidateQueries({ queryKey: CASHBOXES_QUERY_KEY });
      }
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
};

export const getCashboxById = (
  cashboxes: Cashbox[],
  id?: number | null,
): Cashbox | undefined => {
  if (typeof id !== 'number') {
    return undefined;
  }
  return cashboxes.find(cashbox => cashbox.id === id);
};

export const findCashboxByPaymentMethodId = (
  cashboxes: Cashbox[],
  methodId: number,
): Cashbox | undefined => {
  return cashboxes.find(cashbox =>
    cashbox.payment_methods.some(paymentMethod => paymentMethod.id === methodId),
  );
};

export type CashboxMethodMap = Record<number, number>;

export const pluckCashboxIdsFromMethods = (
  cashboxes: Cashbox[],
): CashboxMethodMap => {
  const map: CashboxMethodMap = {};
  cashboxes.forEach(cashbox => {
    cashbox.payment_methods.forEach(paymentMethod => {
      map[paymentMethod.id] = cashbox.id;
    });
  });
  return map;
};
